/**
 * Auto-classification suggestion — frontend wire shapes (task 204).
 *
 * Mirrors the contract the backend lane lands at
 * `GET /api/v1/events/:eventId/suggestion` and
 * `POST /api/v1/events/:eventId/suggestion/promote`. Kept in a dedicated
 * file so the WhyPanel + PatternExtractionPrompt + AppTitleLogPage all
 * read the same source of truth.
 *
 * `aiCallLogId` and `modelVersion` are admin-only and gated server-side;
 * non-admin payloads have them as `null`. The frontend renders the
 * diagnostics expander when they are present and a `diagnostics` flag is
 * also passed in by the parent surface.
 */

export type SuggestionPass = 'text' | 'vision';

export type SuggestionStatus =
  | 'pending'
  | 'accepted'
  | 'overridden'
  | 'dismissed'
  | 'auto_promoted'
  | 'superseded';

export interface SuggestionSignals {
  /** Free-shape per-signal scores (rule, text, vision, neighbours, …). The
   *  WhyPanel surfaces up to five plain-language bullets via
   *  {@link buildWhatISawBullets}; the operator surface renders the raw
   *  object inside the Diagnostics expander. */
  perSignal: Record<string, unknown>;
  weightsVersion: string;
}

export interface SuggestionTopProject {
  id: string;
  name: string;
}

export interface SuggestionResponse {
  id: string;
  eventId: string;
  pass: SuggestionPass;
  status: SuggestionStatus;
  fused: number;
  signals: SuggestionSignals;
  topProject: SuggestionTopProject | null;
  reasoning: string | null;
  /** Screenshot id; the WhyPanel resolves the signed URL via
   *  {@link useScreenshotUrl}. `null` when no screenshot is attached. */
  screenshotId: string | null;
  /** Admin-only — non-admin payloads have this as `null` even when one
   *  exists. */
  aiCallLogId: string | null;
  /** Admin-only — see {@link aiCallLogId}. */
  modelVersion: string | null;
  /** Per-employee count — never per-org / per-team. */
  overrideCount: number;
}

export type PromoteAction = 'accept' | 'override' | 'dismiss';

export interface PromoteRequest {
  action: PromoteAction;
  /** Required for `'override'`, forbidden for `'accept'` and `'dismiss'`. */
  projectId?: string;
  suggestionId: string;
}

/** Rule candidate returned by `extract-rule.ts` and surfaced through the
 *  promote endpoint's response. Shape mirrors the existing
 *  {@link ClassifyRule} where it overlaps with what the
 *  /classification-rules POST accepts. */
export interface RuleCandidate {
  /** Suggested rule name; the admin can edit before saving. */
  name: string;
  /** Suggested priority; falls back to a high default in the prompt. */
  priority: number;
  condition: {
    type: 'pattern' | 'regex' | 'cidr';
    field: 'app' | 'title' | 'url' | 'site' | 'ip' | 'document';
    value: string;
  };
  /** Project this rule will classify to (matches the just-settled
   *  classification's project). */
  projectId: string;
  /** Plain-language description of what the rule will match — surfaced in
   *  the prompt body. */
  description: string;
}

export interface PromoteResponse {
  /** `null` when the action was `'dismiss'`. */
  classifiedEventId: string | null;
  /** Present for `'accept'` / `'override'`; `null` for `'dismiss'` or when
   *  `extract-rule.ts` returned no candidate. */
  ruleCandidate: RuleCandidate | null;
}

export interface NeedsReviewCountResponse {
  count: number;
}

export interface ConfirmAsInternalResponse {
  confirmedCount: number;
}

/** Per-row confidence summary attached to the app-title-log report rows.
 *  The backend lane extends `AppTitleLogRow` with this in the same PR;
 *  rows without a suggestion (e.g. manual classifications) leave it
 *  `null`. The page reads this to decide the StatusDot shape and which
 *  rows pass the `Needs a look` filter. */
export interface AppTitleLogRowConfidence {
  /** Optional — `null` when the row has no fused suggestion (e.g. empty
   *  signals). */
  fused: number | null;
  /** Status of the latest suggestion row attached to any underlying
   *  event in this aggregate. Drives the chip filter predicate. */
  status: SuggestionStatus | null;
  /** Latest suggestion id for the row — opens the WhyPanel via the
   *  events drill-down on click. May be `null` for unclassified rows. */
  suggestionId: string | null;
  /** Anchor event for the WhyPanel. The page uses this when the chip
   *  click drills into the why? panel for a representative event. */
  eventId: string | null;
}

/** UX-side confidence state derived from `fused` + status + target
 *  archived flag. Matches the table in task 204's § Context. */
export type ConfidenceState = 'settled' | 'worth-a-glance' | 'unclassified';

/** Auto-promote confidence threshold; mirrors the backend's
 *  `AI_AUTO_PROMOTE_CONFIDENCE` env knob. Surfaced here so the
 *  StatusDot / chip filter / WhyPanel agree on the same number.
 *  Calibration may ratchet this — when the backend exposes a runtime
 *  value, this constant becomes a fallback. */
export const AI_AUTO_PROMOTE_CONFIDENCE = 0.95;

/** Worth-a-glance lower bound. Below 0.5 the indicator is too noisy to
 *  surface. Not a calibration knob — UX choice. */
export const WORTH_A_GLANCE_FLOOR = 0.5;

export function deriveConfidenceState(args: {
  fused: number | null;
  /** When true, the suggestion's target project is archived — forces
   *  unclassified regardless of `fused`. */
  targetArchived?: boolean;
  /** When true, the suggestion has no signals at all. Forces
   *  unclassified. */
  emptySignals?: boolean;
  /** Optional override of the auto-promote threshold for tests. */
  threshold?: number;
}): ConfidenceState {
  if (args.targetArchived) return 'unclassified';
  if (args.emptySignals) return 'unclassified';
  if (args.fused === null) return 'unclassified';
  const threshold = args.threshold ?? AI_AUTO_PROMOTE_CONFIDENCE;
  if (args.fused >= threshold) return 'settled';
  if (args.fused >= WORTH_A_GLANCE_FLOOR) return 'worth-a-glance';
  return 'unclassified';
}

interface DotStyle {
  shape: 'filled' | 'ring' | 'dash';
  tone: 'enabled' | 'warning' | 'neutral';
  copy: string;
}

export function dotStyleFor(state: ConfidenceState): DotStyle {
  switch (state) {
    case 'settled':
      return {
        shape: 'filled',
        tone: 'enabled',
        copy: 'Set automatically. Tap to change.',
      };
    case 'worth-a-glance':
      return {
        shape: 'ring',
        tone: 'warning',
        copy: 'Best guess — confirm or change.',
      };
    case 'unclassified':
      return {
        shape: 'dash',
        tone: 'neutral',
        copy: 'Not sure yet. Pick one when you have a moment.',
      };
  }
}
